import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useSalesforcePolling, useTheme } from './hooks';
import { LoginPage, DashboardPage, CaseReviewPage, SettingsPage, TranslationPopoutPage } from './pages';
import { OnboardingProvider } from './components/onboarding';

// Protected route wrapper - also runs global polling hooks and provides onboarding
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  // Global polling for SF detection (runs on all protected pages)
  useSalesforcePolling();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <OnboardingProvider>
      {children}
    </OnboardingProvider>
  );
}

// 404 Not Found page
function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-dark-base">
      <div className="bg-white dark:bg-dark-elevated rounded-xl shadow-lg p-8 text-center max-w-md border border-transparent dark:border-dark-border">
        <h1 className="mb-4 text-6xl font-bold text-red-500">404</h1>
        <p className="mb-4 text-xl text-gray-800 dark:text-gray-100">Page Not Found</p>
        <p className="mb-6 text-gray-500 dark:text-gray-400">
          The page you're looking for doesn't exist.
        </p>
        <a
          href="/"
          className="inline-block px-6 py-2 bg-purple-500 dark:bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-600 dark:hover:bg-purple-500 transition-colors"
        >
          Go Home
        </a>
      </div>
    </div>
  );
}

function App() {
  // Apply theme class to document root
  useTheme();

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/review/:caseNumber"
          element={
            <ProtectedRoute>
              <CaseReviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/translation/:caseNumber"
          element={
            <ProtectedRoute>
              <TranslationPopoutPage />
            </ProtectedRoute>
          }
        />

        {/* Redirects */}
        <Route path="/index" element={<Navigate to="/" replace />} />

        {/* 404 catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
